import { Injectable } from '@angular/core';

import { Mutex } from 'async-mutex';
import dayjs from 'dayjs';
import * as jose from 'jose';
import { NgEventBus } from 'ng-event-bus';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  fromEvent,
  startWith,
  Subject,
  Subscription,
} from 'rxjs';
import { map, take } from 'rxjs/operators';

import { UserToken } from '../../models/auth.model';
import {
  Patient_Details,
  PatientListGroup,
  VC_Task,
  VcPatientListItem,
  ViewContent,
} from '../../models/view-content.models/view-content.model';
import {
  Diagnose,
  Task,
  Visit_Record,
} from '../../models/view-content.models/view-content-clinic-domain.model';
import { Place } from '../../models/view-content.models/view-content-organization-domain.model';
import { Case, Patient } from '../../models/view-content.models/view-content-patient-domain.model';
import { CP2_User } from '../../models/view-content.models/view-content-personal-domain.model';
import { Cp2ApiService } from '../cp2-api.service';
import { DataRepositoryService } from '../datarepository.service';
import { AccessFacadeService } from '../facades/access-facade.service';
import { DeviceFacadeService } from '../facades/device-facade.service';
import { SettingsFacadeService } from '../facades/settings-facade.service';

@Injectable({
  providedIn: 'root',
})
export class ViewContentCacheService {
  public static debugMode = false;
  public incomingLocator$ = new Subject<string>();
  public incomingViewContents$ = new Subject<ViewContent<unknown>>();
  public isSyncingDownloads$ = new BehaviorSubject<boolean>(false);
  public isSyncingUploads$ = new BehaviorSubject<boolean>(false);
  private isOnline = navigator.onLine;
  private userToken: UserToken | undefined;
  private user: CP2_User | undefined;
  private locatorSubscription: Subscription | undefined;
  private syncSubscription: Subscription | undefined;

  private vcDownloadTasks: (() => Promise<boolean>)[] = [];
  private vcDownloadTasksMutex = new Mutex();

  public constructor(
    private accessFacade: AccessFacadeService,
    private api: Cp2ApiService,
    private deviceFacade: DeviceFacadeService,
    private eventBus: NgEventBus,
    private repo: DataRepositoryService,
    private settingsFacade: SettingsFacadeService,
  ) {
    this.initOnlineStatus();

    this.accessFacade.userTokens$.subscribe(t => {
      this.log('new userToken value', { t });
      if (!t?.token) {
        this.userToken = undefined;
        this.user = undefined;
        return;
      }
      this.userToken = t.token;

      const decodedToken = jose.decodeJwt(this.userToken.access_token);
      this.user = {
        id: this.userToken.related_user_id,
        uuid: decodedToken['sub'] ?? '',
        surname: decodedToken['family_name'] as string,
        name: decodedToken['given_name'] as string,
      };

      this.syncPendingPuts().then();
    });

    this.deviceFacade.device$.subscribe(d => {
      if (d.device.deviceId)
        this.initIncomingLocatorsListener(d.device.deviceId).then(() => {
          if (ViewContentCacheService.debugMode)
            console.log('VC Cache: incoming locator listener started');
        });
    });
  }

  public async getAllLocatorsForCaseId(caseId: string): Promise<string[]> {
    if (this.userToken?.access_token && this.isOnline)
      return await this.api.getAllLocatorsForCaseId(caseId, this.userToken.access_token);

    const repoLocators = await this.repo.getAllLocatorsForCaseId(caseId);
    if (repoLocators.length) return repoLocators;

    return [];
  }

  public async getCaseList(): Promise<PatientListGroup[]> {
    return await this.buildCaseListFromRepo();
  }

  public async getViewContentForLocator<T>(
    locator: string,
  ): Promise<ViewContent<T> | undefined | null> {
    const repoRes = await this.repo.getFullViewContentForLocator<T>(locator);
    this.log(`ViewContent read from DB for locator:${locator}`, repoRes);

    if (!repoRes) {
      if (!this.userToken?.access_token) {
        console.warn('No token available to fetch ViewContent');
        return null;
      }
      const apiRes = await this.api.getVcForLocator<T>(
        locator,
        this.userToken.access_token,
        undefined,
        {
          send_history: 'false',
        },
      );
      this.log(`ViewContent read from API for locator:${locator}`, repoRes);
      if (!apiRes) return null;

      if (this.user) {
        const updateRes = await this.repo.syncViewContent(
          apiRes,
          this.user.id,
          this.userToken.access_token,
        );
        this.log('getViewContentForLocator: vc updated in repo', {
          apiRes,
          updateRes,
        });

        return updateRes;
      }

      return apiRes;
    }

    return repoRes;
  }

  public async getViewContentsForLocator<T>(
    locator: string,
  ): Promise<ViewContent<T>[] | undefined | null> {
    return await this.repo.getBatchFullViewContentsForLocator<T>(locator);
  }

  public async saveViewContent<T>(vc: ViewContent<T>): Promise<ViewContent<T> | undefined> {
    const demoModeActive = await firstValueFrom(
      this.settingsFacade.settings$.pipe(
        take(1),
        map(settings => settings.demoMode),
      ),
    );

    if (this.isOnline && !demoModeActive) {
      try {
        if (!this.userToken?.access_token) {
          return;
        }

        await this.api.putViewContent<T>(vc, this.userToken.access_token);
      } catch (e) {
        if (this.user) {
          await this.repo.createOrUpdateViewContent(vc, this.user);
          await this.createPendingPutEntry(vc);
        }

        return vc;
      }
    } else {
      if (this.user) {
        await this.repo.createOrUpdateViewContent(vc, this.user);

        if (!demoModeActive) await this.createPendingPutEntry(vc);

        this.incomingViewContents$.next(vc);
      }
    }

    return;
  }

  public async getPatientDetails(caseId: string): Promise<Patient_Details | undefined> {
    const vcCase = await this.repo.getFullViewContentForLocator<Case>(`case.details.${caseId}`);
    const vcPatient = await this.repo.getFullViewContentForLocator<Patient>(
      `patient.details.${vcCase?.related_patient_id}`,
    );
    const vcPlace = await this.repo.getFullViewContentForLocator<Place>(
      `case.overview.place.${caseId}`,
    );
    const vcDiagnose = await this.repo.getFullViewContentForLocator<Diagnose>(
      `case.overview.diagnosis.${caseId}`,
    );

    if (vcCase && vcPatient && vcPlace && vcDiagnose) {
      return {
        case: vcCase,
        patient: vcPatient,
        current_place: vcPlace,
        last_diagnosis: vcDiagnose,
      };
    }

    return;
  }

  private async createPendingPutEntry<T>(vc: ViewContent<T>): Promise<void> {
    // Find out the exact timestamp for the pending PUT
    const last = await this.repo.getFullViewContentForLocator(vc.locator);
    this.log('createPendingPutEntry: Fetched full view content from repo', {
      last,
    });

    const lastHistory = last?.history?.sort((e1, e2) =>
      e2.modifiedAt.localeCompare(e1.modifiedAt),
    )[0];
    const timestamp = lastHistory?.modifiedAt ?? dayjs().toISOString();

    this.log('createPendingPutEntry: Computed timestamp for pending PUT', {
      timestamp,
      lastHistory,
    });

    // Create the pending PUT entry
    if (this.user) {
      const pendingPutRes = await this.repo.createPendingPutVc({
        id: -1,
        vcLocator: vc.locator,
        user: this.user,
        timestamp,
      });
      this.log('createPendingPutEntry: Created pending PUT entry in repo', {
        pendingPutRes,
      });
    }
  }

  private async initIncomingLocatorsListener(deviceId: string): Promise<void> {
    if (this.locatorSubscription) this.locatorSubscription.unsubscribe();
    if (this.syncSubscription) this.syncSubscription.unsubscribe();

    this.locatorSubscription = this.eventBus.on(`sse:device/${deviceId}`).subscribe(e => {
      const locator = e.data as string;
      this.log('Incoming locator listener: new locator', locator);
      this.addTaskToPendingDownloads(locator);
      this.incomingLocator$.next(locator);
      this.syncDownloads();
    });
  }

  private initOnlineStatus(): void {
    const online$ = fromEvent(window, 'online').pipe(startWith(navigator.onLine));
    const offline$ = fromEvent(window, 'offline').pipe(startWith(!navigator.onLine));
    const settings$ = this.settingsFacade.settings$;

    combineLatest([online$, offline$, settings$]).subscribe(([, , settings]) => {
      this.isOnline = navigator.onLine && !settings.simulateOffline;

      if (this.isOnline) {
        this.syncPendingPuts().then();
      }
    });
  }

  private async singleDownloadTask(locator: string): Promise<boolean> {
    this.isSyncingDownloads$.next(true);
    try {
      if (!this.userToken?.access_token) return false;
      if (!this.user) return false;

      const deviceId = await firstValueFrom(this.deviceFacade.getDeviceId());

      const apiVc = await this.api.getVcForLocator(locator, this.userToken.access_token, deviceId, {
        send_history: 'false',
      });

      if (!apiVc) return false;

      const syncedVc = await this.repo.syncViewContent(
        apiVc,
        this.user.id,
        this.userToken.access_token,
      );
      this.incomingViewContents$.next(syncedVc || apiVc);

      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      this.isSyncingDownloads$.next(false);
    }
  }

  private addTaskToPendingDownloads(locator: string): void {
    if (locator.startsWith('case.list.')) return;

    const task = async (): Promise<boolean> => await this.singleDownloadTask(locator);
    this.vcDownloadTasksMutex.runExclusive(() => this.vcDownloadTasks.push(task));
  }

  private async syncDownloads(): Promise<void> {
    this.isSyncingDownloads$.next(true);
    try {
      await this.vcDownloadTasksMutex.runExclusive(async () => {
        for (const task of this.vcDownloadTasks) {
          const res = await task();
          if (res) this.vcDownloadTasks = this.vcDownloadTasks.filter(t => t !== task);
        }
      });
    } finally {
      this.isSyncingDownloads$.next(false);
    }
  }

  private async syncPendingPuts(): Promise<void> {
    this.isSyncingUploads$.next(true);
    try {
      // Get all pending operations and log the result
      const pendingPuts = await this.repo.getAllPendingPutVc();
      this.log('syncPendingPuts: Fetched all pending PUT operations', {
        pendingPuts,
      });

      for (const e of pendingPuts) {
        // Get the original viewContent and the corresponding history element
        const viewContent = await this.repo.getFullViewContentForLocator(e.vcLocator);
        this.log('syncPendingPuts: Fetched viewContent for locator from repo', {
          vcLocator: e.vcLocator,
          viewContent,
        });

        const histElement = viewContent?.history?.find(h => h.modifiedAt === e.timestamp);
        this.log('syncPendingPuts: Found matching history element', {
          histElement,
        });

        if (viewContent && histElement) {
          viewContent.data = histElement.data;
          viewContent.updated_at = histElement.modifiedAt;
          viewContent.history = undefined;
          viewContent.form = undefined;
          viewContent.i18n = undefined;

          this.log('syncPendingPuts: Prepared viewContent for PUT request', {
            viewContent,
            existsUserToken: !!this.userToken,
          });

          if (this.userToken) {
            try {
              // Send the PUT request to the API and log the response
              const apiRes = await this.api.putViewContent(
                viewContent,
                this.userToken?.access_token,
              );
              this.log('syncPendingPuts: Sent PUT request to API', {
                apiRes,
              });

              // Delete the pending PUT from the database and log the response
              const dbDeleteRes = await this.repo.deletePendingPutVc(e.id);
              this.log('syncPendingPuts: Deleted pending PUT entry from repo', { dbDeleteRes });
            } catch (error) {
              // Log any errors that occur during the PUT request or delete operation
              this.log('syncPendingPuts: Error during PUT or DB delete operation', {
                error,
                viewContent,
              });
            }
          }
        }
      }

      this.log('syncPendingPuts: Completed all pending PUT operations');
    } finally {
      this.isSyncingUploads$.next(false);
    }
  }

  private async buildCaseListFromRepo(): Promise<PatientListGroup[]> {
    const listIdentifiers = await this.repo.getAllCaseListEntries();
    const repoRes: PatientListGroup[] = listIdentifiers.map(li => ({
      listIdentifier: li,
      patients: [],
    }));

    const allCaseId = await this.repo.getAllCaseId();

    for (const caseId of allCaseId) {
      const vcVisitRecord = await this.repo.getFullViewContentForLocator<Visit_Record>(
        `case.overview.visit_record.${caseId}`,
      );
      const allLocators = await this.repo.getAllLocatorsForCaseId(caseId);
      const taskLocators = allLocators.filter(l => l.startsWith('case.communication.tasks'));
      const vcTasks: VC_Task[] = (
        await Promise.all(
          taskLocators.map(async l => await this.repo.getFullViewContentForLocator<Task>(l)),
        )
      ).filter((e): e is VC_Task => e !== null);

      const patientDetails = await this.getPatientDetails(caseId);

      if (patientDetails) {
        const patientListItem: VcPatientListItem = {
          patient_details: patientDetails,
          visit_record: vcVisitRecord ?? undefined,
          tasks: vcTasks,
        };
        const listIds = await this.repo.getListIdForCase(patientDetails.case.related_case_id);

        for (const listId of listIds) {
          const list = repoRes.find(l => l.listIdentifier.id === listId);
          list?.patients.push(patientListItem);
        }
      }
    }

    return repoRes;
  }

  private log(...l: any): void {
    if (ViewContentCacheService.debugMode) {
      console.log(...l);
    }
  }
}
